// src/services/v2-chat-api.ts
// SSE-based API client for V2 Chat endpoints (agent runtime)

import { getCommonHeaders, tokenManager } from './api';
import type { V2StreamEvent, UploadResult, ArtifactVersionSummary, ModelOption } from '@/types/chat';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.zamino.com';

export type SSEEventHandler = (event: V2StreamEvent) => void;

/**
 * Open a POST-based SSE connection to the given endpoint.
 * Parses incoming SSE events and forwards them to the handler.
 * Resolves when the stream ends; rejects on HTTP or network errors.
 * Exported for other streaming clients (wiki-api's Discuss view).
 */
export async function connectSSE(
  endpoint: string,
  body: Record<string, unknown>,
  onEvent: SSEEventHandler,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await getCommonHeaders()),
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(errorData.error || errorData.details || `HTTP ${response.status}`);
  }

  if (!response.body) {
    throw new Error('No response body for SSE stream');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          try {
            const parsed = JSON.parse(data) as V2StreamEvent;
            onEvent(parsed);
          } catch {
            // Skip malformed JSON
          }
        }
        // Comments (:keepalive) and event: lines are silently ignored
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export const v2ChatApi = {
  /**
   * Start a new chat via the agent runtime. Streams SSE events.
   * The backend creates a thread and emits a `thread_created` event
   * before running the agent loop.
   */
  startChat: (
    params: {
      message: string;
      upload_ids?: string[];
      agent_id?: string;
      model_override?: string;
      enable_cache_diagnostics?: boolean;
    },
    onEvent: SSEEventHandler,
    signal?: AbortSignal,
  ): Promise<void> => connectSSE('/api/v2/chat/start', params, onEvent, signal),

  /**
   * Send a message in an existing thread via the agent runtime.
   * Streams SSE events for tool activity and the assistant response.
   */
  sendMessage: (
    params: { thread_id: string; message: string; upload_ids?: string[] },
    onEvent: SSEEventHandler,
    signal?: AbortSignal,
  ): Promise<void> => connectSSE('/api/v2/chat/message', params, onEvent, signal),

  /**
   * Stop an in-flight agent run on a thread. Blocks until the backend has
   * observed the abort and finalized (up to ~15s), so the caller can safely
   * unlock the input once this resolves. Returns the terminal stop_reason
   * reported by the agent loop.
   */
  abortStream: async (
    thread_id: string,
  ): Promise<{ session_id: string; status: string; stop_reason: string | null }> => {
    const response = await fetch(`${API_BASE_URL}/api/v2/chat/abort`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await getCommonHeaders()),
      },
      body: JSON.stringify({ thread_id }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Abort failed' }));
      throw new Error(errorData.error || errorData.details || `HTTP ${response.status}`);
    }

    return response.json();
  },

  /**
   * Upload an image file for attachment to a chat message.
   * Returns upload metadata including the upload_id to reference when sending.
   */
  uploadFile: async (file: File): Promise<UploadResult> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/api/v2/chat/upload`, {
      method: 'POST',
      headers: await getCommonHeaders(),
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(errorData.details || errorData.error || `HTTP ${response.status}`);
    }

    const result = await response.json();
    return result.data as UploadResult;
  },

  /**
   * Get the download URL for an artifact.
   * Returns the full URL that can be used in an anchor tag or window.open().
   */
  getArtifactDownloadUrl: (artifactId: string): string => {
    return `${API_BASE_URL}/api/v2/chat/artifacts/${artifactId}/download`;
  },

  /**
   * Get the preview URL for an artifact (inline rendering in iframe).
   */
  getArtifactPreviewUrl: (artifactId: string): string => {
    return `${API_BASE_URL}/api/v2/chat/artifacts/${artifactId}/preview`;
  },

  /**
   * Download an artifact as a blob for saving.
   */
  downloadArtifact: async (artifactId: string): Promise<Blob> => {
    const response = await fetch(`${API_BASE_URL}/api/v2/chat/artifacts/${artifactId}/download`, {
      method: 'GET',
      headers: await getCommonHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Download failed: HTTP ${response.status}`);
    }

    return response.blob();
  },

  /**
   * Fetch the canonical list of models the system supports.
   * Source of truth: bizguard-backend/src/services/llm/models-registry.ts.
   * Consumed by the dev/internal model-override dropdown on the chat page.
   */
  listModels: async (): Promise<ModelOption[]> => {
    const response = await fetch(`${API_BASE_URL}/api/v2/chat/models`, {
      method: 'GET',
      headers: await getCommonHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch models: HTTP ${response.status}`);
    }

    const json = await response.json();
    return json.data as ModelOption[];
  },

  /**
   * Get all versions of an artifact group.
   */
  getArtifactVersions: async (artifactGroupId: string): Promise<ArtifactVersionSummary[]> => {
    const response = await fetch(`${API_BASE_URL}/api/v2/chat/artifacts/${artifactGroupId}/versions`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(await getCommonHeaders()),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get versions: HTTP ${response.status}`);
    }

    const data = await response.json();
    return data.versions as ArtifactVersionSummary[];
  },

  /**
   * Get the auth token for embedding in iframe src URLs. Synchronous because
   * it is read during render; callers doing their own `fetch` should
   * `await ensureFreshAccessToken()` first, since no interceptor covers them.
   */
  getAuthToken: (): string | null => {
    return tokenManager.get();
  },
};

export default v2ChatApi;
